/**
 * Sign in — DESIGN_GUIDELINES.md §9.5.
 *
 * Deliberately plain: the brand should not compete with a login task. One card, a logo, two
 * fields and one primary action.
 *
 * Colour note: the primary button uses Mantine's `hfGreen` primary at shade 7 (`#00873C`),
 * not the brand green-500, because white text on `#00B140` is 2.85:1 and fails WCAG AA
 * (DESIGN_GUIDELINES.md §3.2).
 */

import { useState } from 'react';
import { Alert, Button, PasswordInput, Stack, Text, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { ApiError, login, type SessionUser } from '../lib/api';
import { AppCanvas } from '../components/AppCanvas';
import { Logo } from '../components/Logo';

interface LoginPageProps {
  onSignedIn: (user: SessionUser) => void;
}

export function LoginPage({ onSignedIn }: LoginPageProps) {
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm({
    initialValues: { email: '', password: '' },
    validate: {
      email: (value) => (/^\S+@\S+\.\S+$/.test(value) ? null : 'Enter a valid email address'),
      password: (value) => (value.length > 0 ? null : 'Enter your password'),
    },
  });

  async function handleSubmit(values: { email: string; password: string }) {
    setSubmitting(true);
    setError(null);
    try {
      onSignedIn(await login(values.email, values.password));
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Could not sign in. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppCanvas>
      <div className="mx-auto w-full max-w-sm">
        <Stack gap="lg">
          <Stack gap="xs" align="center">
            <Logo size={44} />
            <Title order={1} ta="center">
              HelloFresh Recipe Planner
            </Title>
            <Text size="sm" c="var(--ff-muted)" ta="center">
              Sign in to see your recipes and shopping lists.
            </Text>
          </Stack>

          <form onSubmit={form.onSubmit(handleSubmit)} noValidate>
            <Stack gap="md">
              <TextInput
                label="Email"
                placeholder="you@example.com"
                autoComplete="email"
                required
                {...form.getInputProps('email')}
              />
              <PasswordInput
                label="Password"
                placeholder="Your password"
                autoComplete="current-password"
                required
                {...form.getInputProps('password')}
              />

              {error ? (
                <Alert
                  color="red"
                  variant="light"
                  title="Could not sign in"
                  role="alert"
                  aria-live="polite"
                >
                  {error}
                </Alert>
              ) : null}

              <Button type="submit" loading={submitting} fullWidth size="md">
                Sign in
              </Button>
            </Stack>
          </form>

          <Text size="xs" c="var(--ff-muted)" ta="center">
            Development build — seeded accounts are listed in <code>scripts/seed.sql</code>.
          </Text>
        </Stack>
      </div>
    </AppCanvas>
  );
}
